import { SEARCH_PAGES } from 'modules/quickActionsMenu/constants';
import { useQuickActionsMenuContext } from 'modules/quickActionsMenu/contexts';
import { PAGE } from 'modules/quickActionsMenu/types';
import { getSearchPage } from 'modules/quickActionsMenu/utils';
import { useEffect, useState } from 'react';
import { Modal, MODAL_SIZES } from 'ui/modal';
import { CampaignMediums, CampaignSearch, CreateCampaignModal } from '../campaigns';
import { CommandsGroup, ItemsGroup, NavigationGroup } from '../ItemGroups';
import { CreateWorkflowModal, JourneySearch } from '../journeys';
import { SnippetSearch } from '../snippets';
import { CommandEmpty, CommandList, CommandRoot } from '../Styled';
import { CreateTemplateModal, TemplateMediums, TemplateSearch } from '../templates';
import { UserSearch } from '../users';
import { Footer } from './Footer';
import { SearchInput } from './SearchInput';

export const BaseMenu = () => {
  const [count, setCount] = useState(0);
  const { closeMenu, debouncedSearchTerm, pageState, selectPage, setValue, value } =
    useQuickActionsMenuContext();
  const { page, data } = pageState || {};

  const openPage = (e: KeyboardEvent, pageToOpen?: PAGE, isValidEvent = false) => {
    if (isValidEvent && pageToOpen) {
      e.preventDefault();
      selectPage({ page: pageToOpen, previousPage: page });
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    const isShiftKey = !!e.shiftKey;
    const isQuickActionsSearchInput = (e?.target as HTMLElement).hasAttribute('cmdk-input');
    const isInputEvent = ['input', 'textarea'].includes(
      (e?.target as HTMLElement)?.nodeName?.toLowerCase(),
    );
    const isOpenInnerPageEvent = Boolean(!pageState && isShiftKey && !isInputEvent);

    switch (e.key) {
      /** Tab keystroke should open search pages from home page */
      case 'Tab': {
        const { page: searchPage } = getSearchPage(value) || {};
        const shouldOpenSearchPage = pageState?.page === PAGE.HOME_PAGE && !!searchPage;
        return openPage(e, searchPage, shouldOpenSearchPage);
      }

      /** Navigate back to homepage from inner pages with a SearchInput (ie. search/mediums) */
      case 'Backspace': {
        return openPage(
          e,
          PAGE.HOME_PAGE,
          Boolean(
            pageState?.page !== PAGE.HOME_PAGE &&
              debouncedSearchTerm === '' &&
              isQuickActionsSearchInput,
          ),
        );
      }

      /** Open QuickActionsMenu from any page (avoid events from inputs) */
      case '/': {
        return openPage(e, PAGE.HOME_PAGE, Boolean(!pageState && !isInputEvent));
      }

      /** Open QuickActionsMenu inner page from any page */
      case SEARCH_PAGES[PAGE.CAMPAIGN_SEARCH].key:
        return openPage(e, PAGE.CAMPAIGN_SEARCH, isOpenInnerPageEvent);
      case SEARCH_PAGES[PAGE.JOURNEY_SEARCH].key:
        return openPage(e, PAGE.JOURNEY_SEARCH, isOpenInnerPageEvent);
      case SEARCH_PAGES[PAGE.SNIPPET_SEARCH].key:
        return openPage(e, PAGE.SNIPPET_SEARCH, isOpenInnerPageEvent);
      case SEARCH_PAGES[PAGE.TEMPLATE_SEARCH].key:
        return openPage(e, PAGE.TEMPLATE_SEARCH, isOpenInnerPageEvent);
      case SEARCH_PAGES[PAGE.USER_SEARCH].key:
        return openPage(e, PAGE.USER_SEARCH, isOpenInnerPageEvent);

      /** Ignore everything else */
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [pageState, debouncedSearchTerm, value]);

  if (!page) {
    return null;
  }

  switch (page) {
    case PAGE.JOURNEY_CREATION:
      return <CreateWorkflowModal />;
    case PAGE.TEMPLATE_CREATION:
      return <CreateTemplateModal id={data?.templateMedium} onClose={closeMenu} />;
    case PAGE.CAMPAIGN_CREATION:
      return <CreateCampaignModal onClose={closeMenu} />;
    default:
      return (
        <Modal
          withPortal
          onClose={closeMenu}
          id="cmdkMenu"
          isModalOpen
          size={MODAL_SIZES.QUICK_ACTIONS}
        >
          <CommandRoot
            label="Quick Actions Menu"
            loop
            onValueChange={setValue}
            shouldFilter
            value={value}
          >
            <SearchInput page={page} />
            <CommandList>
              {page === PAGE.HOME_PAGE && (
                <>
                  <CommandEmpty>No matching results</CommandEmpty>
                  <ItemsGroup />
                  <CommandsGroup />
                  <NavigationGroup />
                </>
              )}
              {page === PAGE.CAMPAIGN_SEARCH && <CampaignSearch setCount={setCount} />}
              {page === PAGE.JOURNEY_SEARCH && <JourneySearch setCount={setCount} />}
              {page === PAGE.SNIPPET_SEARCH && <SnippetSearch setCount={setCount} />}
              {page === PAGE.TEMPLATE_SEARCH && <TemplateSearch setCount={setCount} />}
              {page === PAGE.USER_SEARCH && <UserSearch setCount={setCount} />}
            </CommandList>
            {page === PAGE.CAMPAIGN_MEDIUMS && <CampaignMediums />}
            {page === PAGE.TEMPLATE_MEDIUMS && <TemplateMediums />}
            <Footer count={count} />
          </CommandRoot>
        </Modal>
      );
  }
};
